/**
 * ListingCard - Listing-Aware Property Card Component
 * 
 * Displays property listings with full listing data including:
 * - Featured badge (if listing is featured)
 * - Listing type badge (Sale/Rent/Auction)
 * - Status indicator (Active, Under Offer, Sold, etc.)
 * - Proper pricing based on listing type
 * - Rental terms for rentals
 * - Auction countdown for auctions
 * - Listing agent information
 * - Wishlist button for saving properties
 */

'use client';

import { WishlistButton } from '@/components/customer/WishlistButton';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { FormatPrice } from '@/components/ui/FormatPrice';
import type { FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import { Bath, Bed, Calendar, Clock, Gavel, MapPin, Maximize, User } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { FeaturedBadge } from './FeaturedBadge';
import type { ListingCardData } from './types';
import {
  getListingStatusColor,
  getListingStatusLabel,
  getListingTypeColor,
  getListingTypeLabel,
} from './types';

export interface ListingCardProps {
  listing: ListingCardData;
  href: string;
  variant?: 'default' | 'compact' | 'featured';
  showAgent?: boolean;
  /** Whether to show the agent avatar with name popover on the image - defaults to true */
  showAgentAvatar?: boolean;
  showStatus?: boolean;
  showFeaturedBadge?: boolean;
  /** Whether to show the wishlist (heart) button - defaults to true */
  showWishlistButton?: boolean;
  /** Entity type to save to wishlist - PROPERTY saves the property, LISTING saves the listing */
  wishlistEntityType?: 'PROPERTY' | 'LISTING';
  locale?: string;
  className?: string;
}

export function ListingCard({
  listing,
  href,
  variant = 'default',
  showAgent = false,
  showAgentAvatar = true,
  showStatus = true,
  showFeaturedBadge = true,
  showWishlistButton = true,
  wishlistEntityType = 'PROPERTY',
  locale = 'en',
  className = '',
}: ListingCardProps) {
  const { property, location, listingType, status, price, rentalTerms, auctionDetails, listingAgent, featuredPlacements } = listing;
  
  // Determine the entity ID for wishlist based on the configured entity type
  const wishlistEntityId = wishlistEntityType === 'LISTING' ? listing.listingId : property.id;
  
  // Determine if this is a special status that should be prominently displayed
  const hasSpecialStatus = ['UNDER_OFFER', 'SOLD', 'LEASED', 'COMING_SOON'].includes(status);
  
  // Get the primary featured placement (highest priority with badge variant)
  const primaryFeatured = showFeaturedBadge && featuredPlacements 
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;
  
  // Determine if we should use custom display text or format with FormatPrice
  const hasCustomPriceText = !!listing.displayPriceText;
  const isAuction = listingType === 'AUCTION';
  
  // Format location string
  const locationString = [
    location?.suburb,
    location?.city,
    location?.district,
    location?.neighbourhood,
    location?.region,
  ].filter(Boolean).join(', ') || location?.name || 'Location TBA';

  const cardClasses = `
    bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl 
    transition-all duration-300 group
    ${variant === 'compact' ? 'flex flex-row' : ''}
    ${variant === 'featured' ? 'ring-2 ring-primary' : ''}
    ${className}
  `.trim();

  return (
    <Link href={href} className="block no-underline text-inherit">
      <div className={cardClasses}>
        {/* Image Container */}
        <div className={`relative overflow-hidden bg-gray-100 ${
          variant === 'compact' ? 'w-48 shrink-0 h-full' : 'w-full aspect-4/3'
        }`}>
          {property.primaryImageUrl ? (
            <Image
              src={property.primaryImageUrl}
              alt={property.name}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200">
              <span>No Image</span>
            </div>
          )}
          
          {/* Wishlist Button */}
          {showWishlistButton && (
            <div className="absolute top-3 left-3 z-20">
              <WishlistButton
                entityType={wishlistEntityType}
                entityId={wishlistEntityId}
                variant="solid"
                size="sm"
              />
            </div>
          )}
          
          {/* Listing Type Badge -- replaced with "Sold" when listing is sold */}
          <div className={`absolute ${showWishlistButton ? 'top-3 left-12' : 'top-3 left-3'} px-2.5 py-1 text-xs font-semibold rounded ${
            status === 'SOLD' ? 'bg-gray-700 text-white' : getListingTypeColor(listingType)
          }`}>
            {status === 'SOLD' ? 'Sold' : getListingTypeLabel(listingType)}
          </div>
          
          {/* Featured Badge - positioned top-right if no special status, otherwise below status */}
          {primaryFeatured?.badgeVariant && !hasSpecialStatus && (
            <div className="absolute top-3 right-3 z-10">
              <FeaturedBadge
                variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
                label={primaryFeatured.label}
                size="sm"
              />
            </div>
          )}
          
          {/* Status Badge (if special status) */}
          {showStatus && hasSpecialStatus && (
            <div className={`absolute top-3 right-3 px-2.5 py-1 text-xs font-semibold rounded ${getListingStatusColor(status)}`}>
              {getListingStatusLabel(status)}
            </div>
          )}
          
          {/* Featured Badge - positioned below status badge when special status exists */}
          {primaryFeatured?.badgeVariant && hasSpecialStatus && (
            <div className="absolute top-12 right-3 z-10">
              <FeaturedBadge
                variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
                label={primaryFeatured.label}
                size="sm"
              />
            </div>
          )}
          
          {/* Auction Date Banner */}
          {listingType === 'AUCTION' && auctionDetails?.auctionDate && (
            <div className="absolute bottom-0 left-0 right-0 bg-amber-600/90 text-white px-3 py-1.5 flex items-center gap-2">
              <Gavel size={14} />
              <span className="text-xs font-medium">
                Auction: {new Date(auctionDetails.auctionDate).toLocaleDateString(locale, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}
          
          {/* Agent Avatar with Name Popover */}
          {showAgentAvatar && listingAgent && variant !== 'compact' && (
            <div 
              className={`absolute ${listingType === 'AUCTION' && auctionDetails?.auctionDate ? 'bottom-10' : 'bottom-3'} right-3 z-10 group/agent`}
            >
              <div className="relative">
                {/* Avatar */}
                {listingAgent.avatar ? (
                  <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white shadow-md transition-transform duration-200 group-hover/agent:scale-110">
                    <Image
                      src={listingAgent.avatar}
                      alt={listingAgent.name}
                      width={36}
                      height={36}
                      className="object-cover object-top w-full h-full"
                    />
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gray-700 ring-2 ring-white shadow-md flex items-center justify-center transition-transform duration-200 group-hover/agent:scale-110">
                    <User size={16} className="text-white" />
                  </div>
                )}
                
                {/* Name Popover - appears on hover */}
                <div className="absolute bottom-full right-0 mb-2 opacity-0 invisible group-hover/agent:opacity-100 group-hover/agent:visible transition-all duration-200 pointer-events-none">
                  <div className="bg-gray-900 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
                    <p className="font-semibold">{listingAgent.name}</p>
                    {listingAgent.jobTitle && (
                      <p className="text-gray-300 font-normal">{listingAgent.jobTitle}</p>
                    )}
                    {/* Arrow */}
                    <div className="absolute top-full right-3 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`p-4 flex flex-col gap-2 ${variant === 'compact' ? 'flex-1' : ''}`}>
          {/* Property Type & Location */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {listing.propertyType && (
              <>
                <span className="font-medium text-gray-600">{listing.propertyType.name}</span>
                <span>•</span>
              </>
            )}
            <span className="flex items-center gap-1">
              <MapPin size={12} />
              {locationString}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-primary transition-colors">
            {listing.mainHeadline || property.name}
          </h3>

          {/* Price - uses FormatPrice for automatic currency conversion */}
          <div className="text-xl font-bold text-primary">
            {hasCustomPriceText ? (
              // Use custom display text from API (e.g., "Price on Application")
              <span>{listing.displayPriceText}</span>
            ) : isAuction && auctionDetails ? (
              // Auction: show guide price range with currency conversion
              <span className="flex items-center gap-1">
                {auctionDetails.guidePriceMin && (
                  <FormatPrice
                    amount={auctionDetails.guidePriceMin}
                    currency={auctionDetails.currency || DEFAULT_CURRENCY}
                    size="md"
                    priceOnRequestText="Guide TBA"
                  />
                )}
                {auctionDetails.guidePriceMin && auctionDetails.guidePriceMax && (
                  <span className="mx-1">-</span>
                )}
                {auctionDetails.guidePriceMax && !auctionDetails.guidePriceMin && (
                  <span className="mr-1">Up to</span>
                )}
                {auctionDetails.guidePriceMax && (
                  <FormatPrice
                    amount={auctionDetails.guidePriceMax}
                    currency={auctionDetails.currency || DEFAULT_CURRENCY}
                    size="md"
                    priceOnRequestText=""
                  />
                )}
                {!auctionDetails.guidePriceMin && !auctionDetails.guidePriceMax && (
                  <span>Guide price TBA</span>
                )}
              </span>
            ) : price ? (
              // Standard price with currency conversion
              <span className="flex items-center">
                <FormatPrice
                  amount={price.amount}
                  currency={price.currency}
                  frequency={price.frequency ?? null}
                  size="md"
                  priceOnRequestText="Price on request"
                />
              </span>
            ) : (
              <span>Price on request</span>
            )}
            {rentalTerms?.furnishedStatus && rentalTerms.furnishedStatus !== 'UNFURNISHED' && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({rentalTerms.furnishedStatus.toLowerCase().replace('_', ' ')})
              </span>
            )}
          </div>

          {/* Key Details */}
          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
            {property.bedrooms !== null && property.bedrooms > 0 && (
              <div className="flex items-center gap-1">
                <Bed size={14} className="text-gray-400" />
                <span>{property.bedrooms} {property.bedrooms === 1 ? 'bed' : 'beds'}</span>
              </div>
            )}
            
            {property.bathrooms !== null && property.bathrooms > 0 && (
              <div className="flex items-center gap-1">
                <Bath size={14} className="text-gray-400" />
                <span>{property.bathrooms} {property.bathrooms === 1 ? 'bath' : 'baths'}</span>
              </div>
            )}
            
            {property.totalFloorArea !== null && property.totalFloorArea > 0 && (
              <div className="flex items-center gap-1">
                <Maximize size={14} className="text-gray-400" />
                <span>{property.totalFloorArea.toLocaleString()} {property.totalFloorAreaUnit?.toLowerCase() || 'sqm'}</span>
              </div>
            )}
          </div>

          {/* Rental-specific info */}
          {rentalTerms && (
            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
              {rentalTerms.minLeaseMonths && (
                <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                  <Clock size={10} />
                  Min {rentalTerms.minLeaseMonths} months
                </span>
              )}
              {rentalTerms.petPolicy && rentalTerms.petPolicy !== 'NO_PETS' && (
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                  Pets OK
                </span>
              )}
            </div>
          )}

          {/* Available date */}
          {listing.availableDate && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Calendar size={12} />
              <span>
                Available: {new Date(listing.availableDate).toLocaleDateString(locale, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}

          {/* Agent Info (optional) */}
          {showAgent && listingAgent && variant !== 'compact' && (
            <div className="flex items-center gap-2 pt-2 mt-auto border-t border-gray-100">
              {listingAgent.avatar ? (
                <Image
                  src={listingAgent.avatar}
                  alt={listingAgent.name}
                  width={32}
                  height={32}
                  className="rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <User size={14} className="text-gray-500" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{listingAgent.name}</p>
                {listingAgent.jobTitle && (
                  <p className="text-xs text-gray-500 truncate">{listingAgent.jobTitle}</p>
                )}
              </div>
            </div>
          )}

          {/* View Details CTA */}
          {variant !== 'compact' && !showAgent && (
            <div className="flex items-center justify-between pt-2 mt-auto border-t border-gray-200 text-sm font-medium text-primary">
              <span>View Details</span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default ListingCard;
